import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';
import Configuration from './Configuration';
import TableDetails from './TableDetails';
import ProfilingInfo from './ProfilingInfo';
import DataQualityInfo from './DataQualityInfo';

const AssetDetails = ({ publishData, productId, hideResults = false }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [dqStatusData, setDqStatusData] = useState({});
  const [dqResultsData, setDqResultsData] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch DQ status and results for all assets when component mounts
  useEffect(() => {
    if (!productId) return;
    
    const fetchDQData = async () => {
      setLoading(true);
      try {
        // Get DQ status for all assets
        const statusResponse = await axios.get(`${process.env.REACT_APP_API_URL}/runQuality/status/${productId}`);
        const statusData = statusResponse.data;
        
        // Get DQ results for all assets
        const resultsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/runQuality/ruleStatus/${productId}`);
        const resultsData = resultsResponse.data;
        
        // Process and organize the data by asset
        const assetKeys = Object.keys(publishData?.AssetInfo || {});
        const processedStatusData = {};
        const processedResultsData = {};
        
        assetKeys.forEach(assetKey => {
          const asset = publishData.AssetInfo[assetKey];
          const assetName = asset.Configuration?.assetName || assetKey;
          
          // Find status for this asset
          const assetJob = statusData.dq_jobs?.find(job => {
            if (job.data_asset_name && assetName) {
              return job.data_asset_name.includes(assetName) || 
                     assetName.includes(job.data_asset_name.split('|')[1]) ||
                     assetName === job.data_asset_name.split('|')[1];
            }
            return false;
          });
          
          processedStatusData[assetKey] = assetJob?.status || 'Unknown';
          
          // Find results for this asset - try multiple matching strategies
          const assetResults = resultsData.find(result => {
            // Strategy 1: Match by exact data_asset_id
            if (result.data_asset_id === asset.id) {
              return true;
            }
            
            // Strategy 2: Match by asset name in the result's rule_name
            if (result.rules && result.rules.length > 0) {
              const firstRule = result.rules[0];
              if (firstRule.rule_name && assetName) {
                const ruleName = firstRule.rule_name;
                
                // Check if assetName is contained in the rule_name
                if (ruleName.includes(assetName) || assetName.includes(ruleName.split('|')[1])) {
                  return true;
                }
              }
            }
            
            // Strategy 3: Match by asset key
            if (result.asset_key === assetKey) {
              return true;
            }
            
            return false;
          });
          
          processedResultsData[assetKey] = assetResults || null;
        });
        
        setDqStatusData(processedStatusData);
        setDqResultsData(processedResultsData);
      } catch (error) {
        console.error('Error fetching DQ data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDQData();
  }, [productId, publishData]);

  if (!publishData?.AssetInfo) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No asset information available.</p>
      </div>
    );
  }

  const assetInfo = publishData.AssetInfo;
  const assetKeys = Object.keys(assetInfo);

  const toggleSection = (assetKey, section) => {
    setExpandedSections(prev => ({
      ...prev,
      [`${assetKey}-${section}`]: !prev[`${assetKey}-${section}`]
    }));
  };
  
  return (
    <div className="space-y-6">
      {assetKeys.map((assetKey) => {
        const asset = assetInfo[assetKey];

        return (
          <div key={assetKey} className="space-y-6">
            {/* Configuration Section */}
            <div>
              <button
                onClick={() => toggleSection(assetKey, 'configuration')}
                className="w-full px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-900 font-semibold text-left flex items-center justify-between hover:bg-gray-50 transition-all duration-200 mb-3"
              >
                <span>Configuration</span>
                {expandedSections[`${assetKey}-configuration`] ? (
                  <ChevronDown size={16} className="transform transition-transform duration-200" />
                ) : (
                  <ChevronRight size={16} className="transform transition-transform duration-200" />
                )}
              </button>
              {expandedSections[`${assetKey}-configuration`] && (
                <div className="ml-4">
                  <Configuration config={asset.Configuration} />
                </div>
              )}
            </div>

            {/* Table Details Section */}
            <div>
              <button
                onClick={() => toggleSection(assetKey, 'tableDetails')}
                className="w-full px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-900 font-semibold text-left flex items-center justify-between hover:bg-gray-50 transition-all duration-200 mb-3"
              >
                <span>Table Details ({asset.tableDetails?.length || 0} columns)</span>
                {expandedSections[`${assetKey}-tableDetails`] ? (
                  <ChevronDown size={16} className="transform transition-transform duration-200" />
                ) : (
                  <ChevronRight size={16} className="transform transition-transform duration-200" />
                )}
              </button>
              {expandedSections[`${assetKey}-tableDetails`] && (
                <div className="ml-4">
                  <TableDetails tableDetails={asset.tableDetails} />
                </div>
              )}
            </div>

            {/* Profiling Info Section */}
            <div>
              <button
                onClick={() => toggleSection(assetKey, 'profilingInfo')}
                className="w-full px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-900 font-semibold text-left flex items-center justify-between hover:bg-gray-50 transition-all duration-200 mb-3"
              >
                <span>Profiling Information ({asset.ProfilingInfo?.length || 0} columns)</span>
                {expandedSections[`${assetKey}-profilingInfo`] ? (
                  <ChevronDown size={16} className="transform transition-transform duration-200" />
                ) : (
                  <ChevronRight size={16} className="transform transition-transform duration-200" />
                )}
              </button>
              {expandedSections[`${assetKey}-profilingInfo`] && (
                <div className="ml-4">
                  <ProfilingInfo profilingInfo={asset.ProfilingInfo} />
                </div>
              )}
            </div>

            {/* DQ Info Section */}
            <div>
              <button
                onClick={() => toggleSection(assetKey, 'dqInfo')}
                className="w-full px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-900 font-semibold text-left flex items-center justify-between hover:bg-gray-50 transition-all duration-200 mb-3"
              >
                <span>Data Quality Rules</span>
                {expandedSections[`${assetKey}-dqInfo`] ? (
                  <ChevronDown size={16} className="transform transition-transform duration-200" />
                ) : (
                  <ChevronRight size={16} className="transform transition-transform duration-200" />
                )}
              </button>
              {expandedSections[`${assetKey}-dqInfo`] && (
                <div className="ml-4">
                  <DataQualityInfo 
                    dqInfo={asset['DQ Info']} 
                    productId={productId} 
                    assetName={asset.Configuration?.assetName || assetKey}
                    dqStatus={dqStatusData[assetKey]}
                    dqResults={dqResultsData[assetKey]}
                    hideResults={hideResults}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export { AssetDetails };